import type { AssetInfo as RendererAsset } from "@cva/renderer";
import { type Asset, type BrandKit, referencedAssetIds, type SceneDocument } from "@cva/shared";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { client, orpc } from "../lib/api";
import { onUploadFinished, startUpload } from "../lib/uploads";
import { type AssetInfo, addAsset, replaceImage } from "./commands";
import { addDevice, offerMockup } from "./mockup-commands";
import type { EditorStore } from "./store";
import {
  addClipFromAsset,
  addDeviceClip,
  videoIntoDevice,
  videoIntoPlaceholder,
} from "./video/commands";
import type { VideoStore } from "./video/store";

/** Everything around the document the editor needs: ids, media, uploads. */
export interface EditorEnv {
  orgId: string;
  documentId: string;
  projectId: string;
  title: string;
  canEdit: boolean;
  canExport: boolean;
  /** Brand kit of the project (null while loading or without a kit). */
  brandKit: BrandKit | null;
  /** The organization has no brand kit (known once loaded). */
  brandMissing: boolean;
  assets: Map<string, AssetInfo>;
  resolveAsset: (assetId: string) => RendererAsset | undefined;
  /** Uploads files and inserts them (at a point, into an image element or onto the timeline). */
  uploadAndInsert: (files: File[], target?: UploadTarget) => void;
}

export interface UploadTarget {
  at?: { x: number; y: number };
  replaceId?: string;
  /** Video documents: main track position (videos, images) or time (music). */
  timeline?: { index?: number; at?: number };
  /** Media library folder the file goes into (the folder open in the panel). */
  folderId?: string | null;
  /** Mockups panel: the screenshot goes into a matching device (in this view). */
  mockup?: { view: string };
}

export const EditorEnvContext = createContext<EditorEnv | null>(null);

export function useEnv(): EditorEnv {
  const env = useContext(EditorEnvContext);
  if (!env) throw new Error("Editor-Umgebung fehlt");
  return env;
}

export function toAssetInfo(asset: Asset): AssetInfo {
  return {
    id: asset.id,
    kind: asset.kind,
    url: asset.url,
    thumbnailUrl: asset.thumbnailUrl,
    width: asset.width,
    height: asset.height,
    name: asset.name,
    durationMs: asset.durationMs,
    media: asset.media,
    cutout: asset.cutout,
    matte: asset.matte,
    status: asset.status,
  };
}

/**
 * Brand kit of the project: loads it and keeps the document's linked values
 * current. Undefined while loading, null when the organization has no kit.
 */
export function useEditorBrand(
  orgId: string,
  projectId: string,
  store: EditorStore,
): BrandKit | null | undefined {
  const kit = useQuery({
    ...orpc.brand.effective.queryOptions({ input: { orgId, projectId } }),
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (kit.data !== undefined) store.getState().setBrand(kit.data?.tokens ?? null);
  }, [kit.data, store]);
  return kit.data;
}

/** Media library (images + graphics) plus every asset the document references. */
export function useEditorAssets(orgId: string, doc: SceneDocument, brandKit: BrandKit | null) {
  const library = useQuery(orpc.media.list.queryOptions({ input: { orgId, limit: 200 } }));
  const known = new Set((library.data?.items ?? []).map((asset) => asset.id));
  const missing = referencedAssetIds(doc).filter((id) => !known.has(id));
  // `combine` keeps a stable array reference while the results do not change.
  const extraData = useQueries({
    queries: missing.map((assetId) => ({
      ...orpc.media.get.queryOptions({ input: { orgId, assetId } }),
      staleTime: 5 * 60_000,
    })),
    combine: (results) => results.map((query) => query.data),
  });
  return useMemo(() => {
    const map = new Map<string, AssetInfo>();
    // Logos and graphics of the brand kit can be dropped onto the canvas as well.
    for (const asset of [
      ...(brandKit?.logos.map((logo) => logo.asset) ?? []),
      ...(brandKit?.graphics ?? []),
    ]) {
      map.set(asset.id, { ...asset });
    }
    for (const asset of library.data?.items ?? []) map.set(asset.id, toAssetInfo(asset));
    for (const asset of extraData) if (asset) map.set(asset.id, toAssetInfo(asset));
    return map;
  }, [library.data, extraData, brandKit]);
}

/** Uploads files, waits until the backend processed them and inserts them. */
export function useUploadAndInsert(
  orgId: string,
  projectId: string,
  store: EditorStore,
  video: VideoStore | null = null,
) {
  const queryClient = useQueryClient();
  const pending = useRef(new Map<string, UploadTarget>());

  useEffect(
    () =>
      onUploadFinished(async (item) => {
        if (!item.assetId) return;
        // The new file shows up in the panels right away (as "wird verarbeitet").
        void queryClient.invalidateQueries({ queryKey: orpc.media.list.key() });
        const target = pending.current.get(item.id);
        if (!target) return;
        pending.current.delete(item.id);
        // Large videos take a while on the server; the activity button shows how far it is.
        const deadline = Date.now() + 15 * 60_000;
        for (let attempt = 0; Date.now() < deadline; attempt++) {
          const asset = await client.media.get({ orgId, assetId: item.assetId }).catch(() => null);
          if (asset?.status === "ready") {
            queryClient.setQueryData(
              orpc.media.get.queryKey({ input: { orgId, assetId: asset.id } }),
              asset,
            );
            await queryClient.invalidateQueries({ queryKey: orpc.media.key() });
            // Video documents: a video dropped onto a mockup or placeholder (or uploaded in the Mockups panel) plays there.
            if (video && asset.kind === "video") {
              if (
                target.replaceId &&
                (videoIntoDevice(store, video, target.replaceId, toAssetInfo(asset)) ||
                  videoIntoPlaceholder(store, video, target.replaceId, toAssetInfo(asset)))
              )
                return;
              if (target.mockup) {
                addDeviceClip(store, video, toAssetInfo(asset), { view: target.mockup.view });
                return;
              }
            }
            // Video documents: videos and music go onto the timeline, images too when dropped there.
            const onTimeline =
              video &&
              (asset.kind === "video" ||
                asset.kind === "audio" ||
                (asset.kind === "image" && target.timeline));
            if (onTimeline) {
              addClipFromAsset(store, video, toAssetInfo(asset), {
                ...(target.timeline?.index !== undefined ? { index: target.timeline.index } : {}),
                ...(target.timeline?.at !== undefined ? { at: target.timeline.at } : {}),
              });
              return;
            }
            if (asset.kind !== "image" && asset.kind !== "svg") {
              toast.info(
                `${asset.name} liegt jetzt in der Mediathek (nur Bilder und Grafiken lassen sich einfügen).`,
              );
              return;
            }
            if (target.mockup && asset.kind === "image") {
              addDevice(store, { screenshot: asset, view: target.mockup.view });
            } else if (target.replaceId) {
              replaceImage(store, target.replaceId, asset.id, asset);
            } else {
              const id = addAsset(store, toAssetInfo(asset), target.at);
              if (asset.kind === "image") offerMockup(store, id, asset);
            }
            return;
          }
          if (asset?.status === "failed") {
            toast.error(asset.error ?? "Die Datei konnte nicht verarbeitet werden");
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, attempt < 30 ? 1000 : 3000));
        }
        toast.info(
          `${item.name} wird noch verarbeitet. Sie erscheint in der Mediathek, sobald sie fertig ist.`,
        );
      }),
    [orgId, queryClient, store, video],
  );

  return useCallback(
    (files: File[], target: UploadTarget = {}) => {
      for (const file of files) {
        const id = startUpload(file, { orgId, projectId, folderId: target.folderId });
        if (id) pending.current.set(id, target);
      }
    },
    [orgId, projectId],
  );
}
